import * as React from "react";

// Persistent site-wide controls: theme, accent color, verbosity, sound.
// The design-reference prototype (app.js) kept all of this in-memory only;
// this context persists to localStorage so choices survive navigation and
// reloads, per the README's explicit requirement.

export type Theme = "light" | "dark";

export interface Accent {
  name: string;
  color: string;
  bg: string;
  // The light pair is unreadable on the dark paper (a cream bg behind a
  // mid-tone text), so each accent also carries a dark-theme pair.
  darkColor: string;
  darkBg: string;
}

export const ACCENTS: Accent[] = [
  { name: "green", color: "#2E5C4E", bg: "#E3ECE6", darkColor: "#6FBF9F", darkBg: "#14261F" },
  { name: "rust", color: "#B5502E", bg: "#F2E4DE", darkColor: "#E27B54", darkBg: "#2E1A12" },
  { name: "blue", color: "#3A5A8C", bg: "#E1E7F0", darkColor: "#7BA3E3", darkBg: "#141C2E" },
  { name: "amber", color: "#8C6A3A", bg: "#F0E9DA", darkColor: "#D9A441", darkBg: "#2A2410" },
];

interface ControlsState {
  theme: Theme;
  // Whether the user has explicitly toggled the theme. Until they do, theme
  // tracks the OS-level `prefers-color-scheme` live (including changes made
  // after the page loaded) — see the matchMedia listener below. This is what
  // "system theme is the default" means: not just "read it once at load".
  themeTouched: boolean;
  accent: Accent;
  // Whether the user has explicitly picked a swatch. Light/dark each have
  // their own default --current/--current-bg (see app.css's :root vs
  // [data-theme="dark"]) — this must stay false until a real pick happens,
  // or the effect below would permanently pin the CSS vars to ACCENTS[0]'s
  // light-mode colors via inline style (higher specificity than the
  // [data-theme="dark"] rule), silently breaking every dark-mode default
  // (code blocks, active nav, primary buttons, ...) even though no accent
  // was ever chosen.
  accentTouched: boolean;
  verbose: boolean;
  soundOn: boolean;
}

interface ControlsContextValue extends ControlsState {
  toggleTheme: () => void;
  setAccent: (accent: Accent) => void;
  toggleVerbose: () => void;
  toggleSound: () => void;
  playSound: (kind: "key" | "toggleOn" | "toggleOff" | "breakpoint") => void;
}

const STORAGE_KEY = "portfolio:controls";

const DEFAULT_STATE: ControlsState = {
  theme: "light",
  themeTouched: false,
  accent: ACCENTS[0]!,
  accentTouched: false,
  verbose: false,
  soundOn: true,
};

function systemPrefersDark(): boolean {
  return (
    typeof window !== "undefined" &&
    (window.matchMedia?.("(prefers-color-scheme: dark)").matches ?? false)
  );
}

function loadInitialState(): ControlsState {
  if (typeof window === "undefined") return DEFAULT_STATE;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<ControlsState>;
      const themeTouched = Boolean(parsed.themeTouched);
      return {
        theme: themeTouched
          ? parsed.theme === "dark"
            ? "dark"
            : "light"
          : systemPrefersDark()
            ? "dark"
            : "light",
        themeTouched,
        accent:
          ACCENTS.find((a) => a.name === parsed.accent?.name) ?? ACCENTS[0]!,
        accentTouched: Boolean(parsed.accentTouched),
        verbose: Boolean(parsed.verbose),
        soundOn: parsed.soundOn ?? true,
      };
    }
  } catch {
    // ignore malformed storage
  }
  // Default to the OS-level theme (system theme), per the design-reference
  // README's explicit requirement.
  return { ...DEFAULT_STATE, theme: systemPrefersDark() ? "dark" : "light" };
}

const ControlsContext = React.createContext<ControlsContextValue | null>(
  null,
);

// Tiny Web Audio blip engine, ported from app.js's Sound module.
function useSoundEngine(enabled: boolean) {
  const ctxRef = React.useRef<AudioContext | null>(null);

  return React.useCallback(
    (kind: "key" | "toggleOn" | "toggleOff" | "breakpoint") => {
      if (!enabled) return;
      try {
        if (!ctxRef.current) {
          ctxRef.current = new (window.AudioContext ||
            (window as unknown as { webkitAudioContext: typeof AudioContext })
              .webkitAudioContext)();
        }
        const c = ctxRef.current;
        const params: Record<
          typeof kind,
          { freq: number; dur: number; type: OscillatorType; gain: number }
        > = {
          key: { freq: 1200, dur: 0.03, type: "square", gain: 0.02 },
          toggleOn: { freq: 880, dur: 0.08, type: "sine", gain: 0.03 },
          toggleOff: { freq: 440, dur: 0.08, type: "sine", gain: 0.03 },
          breakpoint: { freq: 220, dur: 0.12, type: "triangle", gain: 0.04 },
        };
        const p = params[kind];
        const osc = c.createOscillator();
        const g = c.createGain();
        osc.type = p.type;
        osc.frequency.value = p.freq;
        g.gain.value = p.gain;
        osc.connect(g);
        g.connect(c.destination);
        osc.start();
        g.gain.exponentialRampToValueAtTime(0.0001, c.currentTime + p.dur);
        osc.stop(c.currentTime + p.dur);
      } catch {
        // audio not available (e.g. autoplay policy) — fail silently
      }
    },
    [enabled],
  );
}

export function ControlsProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = React.useState<ControlsState>(loadInitialState);
  const playSound = useSoundEngine(state.soundOn);

  // While the user hasn't explicitly toggled the theme, keep following the
  // OS-level preference live (e.g. the system switching to dark mode at
  // night), not just at initial load.
  React.useEffect(() => {
    const mql = window.matchMedia?.("(prefers-color-scheme: dark)");
    if (!mql) return;
    const onChange = (e: MediaQueryListEvent) => {
      setState((s) =>
        s.themeTouched ? s : { ...s, theme: e.matches ? "dark" : "light" },
      );
    };
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);

  React.useEffect(() => {
    document.documentElement.setAttribute("data-theme", state.theme);
    document.documentElement.setAttribute(
      "data-verbose",
      String(state.verbose),
    );
    if (state.accentTouched) {
      const dark = state.theme === "dark";
      document.documentElement.style.setProperty(
        "--current",
        dark ? state.accent.darkColor : state.accent.color,
      );
      document.documentElement.style.setProperty(
        "--current-bg",
        dark ? state.accent.darkBg : state.accent.bg,
      );
    } else {
      // No explicit pick yet — let the CSS cascade's [data-theme="dark"]
      // defaults (distinct from light's) apply instead of pinning them.
      document.documentElement.style.removeProperty("--current");
      document.documentElement.style.removeProperty("--current-bg");
    }
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {
      // storage unavailable (private mode, quota) — non-fatal
    }
  }, [state]);

  const value: ControlsContextValue = {
    ...state,
    toggleTheme: () =>
      setState((s) => {
        playSound(s.theme === "dark" ? "toggleOff" : "toggleOn");
        return {
          ...s,
          theme: s.theme === "dark" ? "light" : "dark",
          themeTouched: true,
        };
      }),
    setAccent: (accent) => {
      playSound("key");
      setState((s) => ({ ...s, accent, accentTouched: true }));
    },
    toggleVerbose: () =>
      setState((s) => {
        playSound(s.verbose ? "toggleOff" : "toggleOn");
        return { ...s, verbose: !s.verbose };
      }),
    toggleSound: () =>
      setState((s) => {
        const next = !s.soundOn;
        return { ...s, soundOn: next };
      }),
    playSound,
  };

  return (
    <ControlsContext.Provider value={value}>
      {children}
    </ControlsContext.Provider>
  );
}

export function useControls(): ControlsContextValue {
  const ctx = React.useContext(ControlsContext);
  if (!ctx) {
    throw new Error("useControls must be used within a ControlsProvider");
  }
  return ctx;
}
